import { CircleCheck, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EntryPath } from '@/Config/app/router/entry.path';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  PasskeySetupError,
  type PasskeySetupErrorKind,
  usePasskeySetup,
} from '@/Domain/hooks/Auth/usePasskeySetup.hook';

const ERROR_MESSAGE_KEYS: Record<PasskeySetupErrorKind, string> = {
  invalid: 'passkey_setup:error_invalid',
  rejected: 'passkey_setup:error_rejected',
  limit: 'passkey_setup:error_limit',
  throttled: 'ERRORS:TOO_MANY_REQUESTS',
  generic: 'passkey_setup:error_generic',
};

export const PasskeySetupPage = () => {
  const { t } = useTranslation(['passkey_setup', 'login', 'ERRORS']);
  const { setUpPasskey, isPending, isSuccess, error } = usePasskeySetup();
  const canRetry = error !== PasskeySetupError.invalid && error !== PasskeySetupError.limit;

  if (isSuccess) {
    return (
      <div className="animate-in fade-in duration-300" data-testid="passkey-setup-success">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-muted">
            <CircleCheck className="h-8 w-8 text-success" aria-hidden="true" />
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">
            {t('passkey_setup:success_title')}
          </h1>
          <p
            className="text-sm text-muted-foreground"
            data-testid="passkey-setup-success-description"
          >
            {t('passkey_setup:success_description', { button: t('login:passkey.button') })}
          </p>
        </div>
        <Button variant="dark" size="lg" className="w-full" asChild>
          <Link to={EntryPath.login} data-testid="passkey-setup-login-link">
            {t('passkey_setup:success_link')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <KeyRound className="h-8 w-8 text-foreground" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('passkey_setup:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('passkey_setup:description')}</p>
      </div>

      <div className="grid gap-5">
        {error && (
          <Alert
            variant="destructive"
            data-testid={`passkey-setup-error-${error}`}
            className="animate-in fade-in duration-200"
          >
            <AlertDescription>{t(ERROR_MESSAGE_KEYS[error])}</AlertDescription>
          </Alert>
        )}

        {canRetry && (
          <>
            <p
              className="text-center text-sm text-muted-foreground"
              data-testid="passkey-setup-hint"
            >
              {t('passkey_setup:prompt_hint')}
            </p>
            <Button
              type="button"
              variant="dark"
              size="lg"
              className="w-full gap-3"
              loading={isPending}
              onClick={setUpPasskey}
              data-testid="passkey-setup-submit"
            >
              {t('passkey_setup:button')}
            </Button>
          </>
        )}

        <div className="border-t border-border pt-4 text-center">
          <Link
            className="text-sm font-medium text-foreground transition-colors hover-underline"
            to={EntryPath.login}
            data-testid="passkey-setup-back"
          >
            {t('passkey_setup:back_to_login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PasskeySetupPage;
